import React, { useState, useEffect, useRef } from "react";
import CommonContainer from "../Container/CommonContainer";
import HVTRegistrationModal from "./HVTRegistrationModal";
import HVTOrderStatusModal from "./HVTOrderStatusModal";
import HVTRescheduleModal from "./HVTRescheduleModal";
import HVTAddPatientModal from "./HVTAddPatientModal";
import CommonPagination from "../../Common/CommonPagination";
import Popover from "../../Common/Popper";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Calendar, ArrowUp, ArrowDown, Eye } from "lucide-react";
import { FaSearch, FaSpinner, FaUser, FaUsers } from "react-icons/fa";
import toast from "react-hot-toast";
import * as HVTRegistrationService from "../../Services/HVTRegistration/HVTRegistrationService";
import { MAX_GROUP_PATIENTS } from "./RegistrationForm/wizardConfig";
import { formatDateTimeAMPM } from "./HVTCommonHelpers";
import "./HVTRegistration.css";

// StartDate/EndDate bind directly to a .NET DateTime on the backend, which
// needs ISO 8601 regardless of the DD-MM-YYYY display format used elsewhere
// in this API's responses.
const formatDate = (date, isEnd = false) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return isEnd ? `${year}-${month}-${day}T23:59:59` : `${year}-${month}-${day}T00:00:00`;
};

const startOfDay = (date) => { const d = new Date(date); d.setHours(0, 0, 0, 0); return d; };
const endOfDay = (date) => { const d = new Date(date); d.setHours(23, 59, 59, 999); return d; };

const displayValue = (val) => (!val || val === "NA" ? "-" : val);

const HVTRegistrationListComponent = () => {
    const [showRegistrationModal, setShowRegistrationModal] = useState(false);

    const [registrationList, setRegistrationList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchLoading, setSearchLoading] = useState(false);

    const [searchText, setSearchText] = useState("");
    const [dateRangeOption, setDateRangeOption] = useState("Today");
    const [startDate, setStartDate] = useState(startOfDay(new Date()));
    const [endDate, setEndDate] = useState(endOfDay(new Date()));
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);

    const [sortColumn, setSortColumn] = useState("VisitDate");
    const [sortDirection, setSortDirection] = useState("desc");

    const [currentPage, setCurrentPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const itemsPerPage = 15;

    const fetchRegistrationList = (page = 1) => {
        setLoading(true);
        setSearchLoading(true);

        const payload = {
            SearchText: searchText.trim(),
            SortColumn: null,
            SortDirection: null,
            Skip: (page - 1) * itemsPerPage,
            Take: itemsPerPage,
            StartDate: formatDate(startDate),
            EndDate: formatDate(endDate, true),
        };

        HVTRegistrationService.GetRegistrationList(payload)
            .then(({ data }) => {
                const rows = data?.IsSuccess && data?.Data?.RegistrationList?.length ? data.Data.RegistrationList : [];
                setRegistrationList(rows);
                setCurrentPage(page);
                setTotalCount(data?.IsSuccess ? (data?.Data?.TotalCount || 0) : 0);
            })
            .catch(() => toast.error("Something went wrong while loading registrations."))
            .finally(() => {
                setLoading(false);
                setSearchLoading(false);
            });
    };

    useEffect(() => {
        fetchRegistrationList(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, sortColumn, sortDirection]);

    const handleDateRangeChange = (e) => {
        const selected = e.target.value;
        setDateRangeOption(selected);

        const today = new Date();
        let start = startOfDay(today);
        let end = endOfDay(today);

        switch (selected) {
            case "Today":
                break;
            case "Yesterday": {
                const yesterday = new Date(today);
                yesterday.setDate(today.getDate() - 1);
                start = startOfDay(yesterday);
                end = endOfDay(yesterday);
                break;
            }
            case "Current Week": {
                const dayOfWeek = today.getDay();
                start = startOfDay(new Date(today));
                start.setDate(today.getDate() - dayOfWeek);
                break;
            }
            case "Last 7 Days":
                start = startOfDay(new Date(today));
                start.setDate(today.getDate() - 6);
                break;
            case "MTD":
                start = startOfDay(new Date(today.getFullYear(), today.getMonth(), 1));
                break;
            case "Last 30 Days":
                start = startOfDay(new Date(today));
                start.setDate(today.getDate() - 29);
                break;
            case "Custom":
                start = startDate;
                end = endDate;
                break;
            default:
                break;
        }

        setStartDate(start);
        setEndDate(end);
    };

    const handleSearch = () => {
        fetchRegistrationList(1);
    };

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === "asc" ? "desc" : "asc");
        } else {
            setSortColumn(column);
            setSortDirection("asc");
        }
    };

    const [viewingBooking, setViewingBooking] = useState(null);
    const [viewLoadingId, setViewLoadingId] = useState(null);
    const [reschedulingBooking, setReschedulingBooking] = useState(null);
    const [rescheduleLoadingId, setRescheduleLoadingId] = useState(null);
    const [addPatientTarget, setAddPatientTarget] = useState(null);
    const [addPatientLoadingId, setAddPatientLoadingId] = useState(null);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [cancellationReason, setCancellationReason] = useState("");
    const [isCancelling, setIsCancelling] = useState(false);

    const hasBookingId = (item) => !!item.BookingId && item.BookingId !== "0";

    const handleViewClick = (item) => {
        setViewLoadingId(item.BookingId);
        HVTRegistrationService.GetBookingDetailsByBookingId(item.BookingId)
            .then(({ data }) => {
                const details = data?.Data?.BookingDetails?.[0];
                if (!data?.IsSuccess || !details) {
                    // toast.error(data?.ResponseMessage || "Unable to load registration details.");
                    toast.error("HVT Error: Unable to load registration details.");
                    return;
                }
                setViewingBooking({ bookingId: item.BookingId, details, item, primaryDetails: data?.Data?.PrimaryRegistrationDetails });
            })
            .catch(() => toast.error("HVT Error: Something went wrong while loading registration details."))
            .finally(() => setViewLoadingId(null));
    };

    const handleRescheduleClick = (item, onSuccess) => {
        setRescheduleLoadingId(item.BookingId);
        HVTRegistrationService.GetBookingDetailsByBookingId(item.BookingId)
            .then(({ data }) => {
                const details = data?.Data?.BookingDetails?.[0];
                if (!data?.IsSuccess || !details) {
                    // toast.error(data?.ResponseMessage || "Unable to load booking details.");
                    toast.error("HVT Error: Unable to load booking details.");
                    return;
                }
                setReschedulingBooking({ item, details, primaryDetails: data?.Data?.PrimaryRegistrationDetails });
                onSuccess?.();
            })
            .catch(() => toast.error("HVT Error: Something went wrong while loading booking details."))
            .finally(() => setRescheduleLoadingId(null));
    };

    const handleAddPatientClick = (item, onSuccess) => {
        setAddPatientLoadingId(item.BookingId);
        Promise.all([
            HVTRegistrationService.GetBookingDetailsByBookingId(item.BookingId),
            HVTRegistrationService.getGroupSiblings(item.BookingId),
        ])
            .then(([{ data }, siblings]) => {
                const details = data?.Data?.BookingDetails?.[0];
                if (!data?.IsSuccess || !details) {
                    // toast.error(data?.ResponseMessage || "Unable to load booking details.");
                    // toast.error(data?.ResponseMessage || "Unable to load booking details.");
                    toast.error("HVT Error: Unable to load booking details.");
                    return;
                }
                if (siblings.length >= MAX_GROUP_PATIENTS) {
                    toast.error(`This booking already has the maximum of ${MAX_GROUP_PATIENTS} patients.`);
                    return;
                }
                setAddPatientTarget({ item, details, siblingCount: siblings.length });
                onSuccess?.();
            })
            .catch(() => toast.error("Something went wrong while loading booking details."))
            .finally(() => setAddPatientLoadingId(null));
    };

    const handleConfirmCancel = () => {
        if (!cancelTarget) return;
        if (!cancellationReason.trim()) {
            toast.error("Please enter a reason for cancellation.");
            return;
        }
        setIsCancelling(true);
        HVTRegistrationService.CancelSchedule(cancelTarget.BookingId, cancellationReason.trim())
            .then(({ data }) => {
                if (!data?.IsSuccess) {
                    // toast.error(data?.ResponseMessage || "Failed to cancel booking.");
                    toast.error("HVT Error: Failed to cancel booking.");
                    return;
                }
                toast.success("Booking cancelled successfully.");
                setCancelTarget(null);
                setCancellationReason("");
                fetchRegistrationList(currentPage);
            })
            .catch(() => toast.error("HVT Error: Something went wrong while cancelling."))
            .finally(() => setIsCancelling(false));
    };

    const SortIcon = ({ column }) => {
        if (sortColumn !== column) return null;
        return sortDirection === "asc" ? <ArrowUp size={12} className="ms-1" /> : <ArrowDown size={12} className="ms-1" />;
    };

    return (
        <>
            <CommonContainer heading="Home Visit" buttonText="Registration" onButtonClick={() => setShowRegistrationModal(true)}>
                <div className="d-flex flex-column flex-lg-row gap-2 justify-content-between align-items-lg-center mb-3">
                    <div className="d-flex flex-column flex-md-row gap-2 align-items-md-center">
                        <select
                            className="form-select"
                            style={{ maxWidth: 200 }}
                            value={dateRangeOption}
                            onChange={handleDateRangeChange}
                        >
                            <option value="Today">Today</option>
                            <option value="Yesterday">Yesterday</option>
                            <option value="Current Week">Current Week</option>
                            <option value="Last 7 Days">Last 7 Days</option>
                            <option value="MTD">MTD</option>
                            <option value="Last 30 Days">Last 30 Days</option>
                            <option value="Custom">Custom</option>
                        </select>

                        {dateRangeOption === "Custom" && (
                            <>
                                <div className="position-relative">
                                    <ReactDatePicker
                                        selected={startDate}
                                        onChange={(date) => setStartDate(startOfDay(date))}
                                        className="form-control"
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="Start Date"
                                        ref={startDateRef}
                                    />
                                    <Calendar size={16} className="position-absolute" style={{ right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer" }} onClick={() => startDateRef.current.setFocus()} />
                                </div>
                                <div className="position-relative">
                                    <ReactDatePicker
                                        selected={endDate}
                                        onChange={(date) => setEndDate(endOfDay(date))}
                                        className="form-control"
                                        dateFormat="dd-MM-yyyy"
                                        placeholderText="End Date"
                                        ref={endDateRef}
                                    />
                                    <Calendar size={16} className="position-absolute" style={{ right: 10, top: "50%", transform: "translateY(-50%)", cursor: "pointer" }} onClick={() => endDateRef.current.setFocus()} />
                                </div>
                            </>
                        )}

                        <button
                            type="button"
                            className="btn custom-sumbitbtn"
                            onClick={() => fetchRegistrationList(1)}
                            disabled={searchLoading}
                        >
                            {searchLoading ? <FaSpinner className="fa-spin" /> : "GO"}
                        </button>
                    </div>

                    <div className="d-flex gap-2">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Search by Name, VID or Booking ID"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleSearch(); } }}
                            style={{ minWidth: 260 }}
                        />
                        <button className="btn custom-sumbitbtn d-flex align-items-center gap-1" onClick={handleSearch} disabled={searchLoading}>
                            {searchLoading ? <FaSpinner className="fa-spin" /> : <FaSearch size={13} />}
                        </button>
                    </div>
                </div>

                {/* <div className="d-flex justify-content-end align-items-center gap-3 mb-1" style={{ fontSize: 12, color: "#666" }}>
                    <span className="d-flex align-items-center gap-1">
                        <FaUser size={12} color="var(--color-primary)" /> Primary booking
                    </span>
                    <span className="d-flex align-items-center gap-1">
                        <FaUsers size={12} color="#495057" /> Group booking
                    </span>
                </div> */}

                <div className="table-responsive">
                    <table className="table table-bordered custom-table">
                        <thead>
                            <tr>
                                <th>Created Date</th>
                                <th role="button" onClick={() => handleSort("BookingId")}>Booking ID <SortIcon column="BookingId" /></th>
                                {/* <th>Group ID</th> */}
                                {/* <th>Prandial ID</th> */}
                                {/* <th role="button" onClick={() => handleSort("Name")}>Patient Name <SortIcon column="Name" /></th> */}
                                <th>Patient Name</th>
                                <th>Mobile</th>
                                <th>VID</th>
                                <th>Visit Date</th>
                                <th>Source</th>
                                <th>Status</th>
                                <th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="10" className="text-center"><FaSpinner className="fa-spin" /></td></tr>
                            ) : registrationList.length === 0 ? (
                                <tr><td colSpan="10" className="text-center">No Data found</td></tr>
                            ) : (
                                registrationList.map((item) => {
                                    const name = item.Name || `${item.FirstName || ""} ${item.LastName || ""}`.trim();
                                    const actionable = hasBookingId(item);
                                    const isViewLoading = viewLoadingId === item.BookingId;
                                    return (
                                        <tr key={item.registration_id}>
                                            <td>{item.Createddate ? formatDateTimeAMPM(item.Createddate) : "-"}</td>
                                            <td>
                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                    <span>{displayValue(item.BookingId)}</span>
                                                </div>
                                            </td>
                                            {/* <td>{displayValue(item.GroupId)}</td> */}
                                            {/* <td>{displayValue(item.PrandialId)}</td> */}
                                            <td>
                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                    {/* {item.IsPrimary ? (
                                                        <Popover content="Primary Booking" fontSize="13px">
                                                            <FaUser size={16} color="var(--color-primary)" />
                                                        </Popover>
                                                    ) : (
                                                        <Popover content={`Child booking of ${(item.GroupId || "").replace(/^G/, "") || "primary booking"}`} fontSize="13px">
                                                            <FaUsers size={16} color="#495057" />
                                                        </Popover>
                                                    )} */}
                                                    <span>{displayValue(name)}</span>
                                                </div>
                                            </td>
                                            <td>{displayValue(item.Mobile)}</td>
                                            <td>{displayValue(item.VID)}</td>
                                            <td>{item.VisitDate && item.VisitDate !== "NA" ? formatDateTimeAMPM(item.VisitDate) : "-"}</td>
                                            <td>{displayValue(item.IsMyClinic ? "My Clinic" : "Customer Location")}</td>
                                            <td>{item.IsCancelled ? (
                                                <Popover content={item.CancellationReason || "No reason provided"} fontSize="13px">
                                                    <span className="hvt-cancelled-pill">Cancelled</span>
                                                </Popover>
                                            ) : (
                                                <span className="hvt-confirmed-pill">Confirmed</span>
                                            )}</td>
                                            <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                              <div className="d-flex align-items-center justify-content-center gap-2">
                                                <span
                                                    className="d-flex"
                                                    title={actionable ? "View Details" : "No booking ID for this record"}
                                                    style={{
                                                        display: "inline-block",
                                                        transition: "transform 0.2s",
                                                        cursor: actionable && !isViewLoading ? "pointer" : "not-allowed",
                                                        opacity: actionable ? 1 : 0.4,
                                                        fontSize: "12px",
                                                    }}
                                                    onMouseEnter={(e) => actionable && !isViewLoading && (e.currentTarget.style.transform = "scale(1.2)")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                                                    onClick={() => actionable && !isViewLoading && handleViewClick(item)}
                                                >
                                                    {isViewLoading ? <FaSpinner className="fa-spin" color="green" size={16} /> : <Eye color="green" size={20} />}
                                                </span>
                                              </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {totalCount > itemsPerPage && (
                    <CommonPagination
                        currentPage={currentPage}
                        setCurrentPage={fetchRegistrationList}
                        totalCount={totalCount}
                        itemsPerPage={itemsPerPage}
                        loading={loading}
                    />
                )}
            </CommonContainer>

            {showRegistrationModal && (
                <div className="modal-backdrop">
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-dialog-centered hvt-modal-dialog hvt-modal-dialog-wide" role="document">
                            <div className="modal-content" style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                                <HVTRegistrationModal onClose={() => { setShowRegistrationModal(false); fetchRegistrationList(currentPage); }} />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {viewingBooking && (
                <HVTOrderStatusModal
                    bookingId={viewingBooking.bookingId}
                    preloadedBooking={viewingBooking.details}
                    preloadedPrimaryDetails={viewingBooking.primaryDetails}
                    item={viewingBooking.item}
                    onClose={() => setViewingBooking(null)}
                    onReschedule={(item) => handleRescheduleClick(item, () => setViewingBooking(null))}
                    onAddPatient={(item) => handleAddPatientClick(item, () => setViewingBooking(null))}
                    onCancel={(item) => { setCancelTarget(item); setViewingBooking(null); }}
                    isRescheduleLoading={rescheduleLoadingId === viewingBooking.bookingId}
                    isAddPatientLoading={addPatientLoadingId === viewingBooking.bookingId}
                />
            )}

            {reschedulingBooking && (
                <HVTRescheduleModal
                    item={reschedulingBooking.item}
                    preloadedBooking={reschedulingBooking.details}
                    onClose={() => setReschedulingBooking(null)}
                    onRescheduled={() => fetchRegistrationList(currentPage)}
                    onBackToDetails={() => {
                        setViewingBooking({
                            bookingId: reschedulingBooking.item.BookingId,
                            details: reschedulingBooking.details,
                            item: reschedulingBooking.item,
                            primaryDetails: reschedulingBooking.primaryDetails,
                        });
                        setReschedulingBooking(null);
                    }}
                />
            )}

            {addPatientTarget && (
                <HVTAddPatientModal
                    item={addPatientTarget.item}
                    preloadedBooking={addPatientTarget.details}
                    siblingCount={addPatientTarget.siblingCount}
                    onClose={() => setAddPatientTarget(null)}
                    onAdded={() => fetchRegistrationList(currentPage)}
                />
            )}

            {cancelTarget && (
                <div className="modal-backdrop">
                    <div className="modal fade show d-block" tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-dialog-centered hvt-modal-dialog" role="document">
                            <div className="modal-content" style={{ maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
                                <div className="hvt-quick-modal">
                                    <div className="modal-header px-3 py-2">
                                        <h5 className="modal-title">Cancel Booking</h5>
                                        <button
                                            type="button"
                                            className="btn-close btn-close-white"
                                            onClick={() => { setCancelTarget(null); setCancellationReason(""); }}
                                            disabled={isCancelling}
                                        ></button>
                                    </div>

                                    <div className="hvt-quick-scroll">
                                        <div className="hvt-quick-section">
                                            <p className="mb-3">Cancel booking for <strong>{cancelTarget.Name || "this patient"}</strong>?</p>
                                            <div className="hvt-field">
                                                <label className="required">Reason for cancellation</label>
                                                <textarea
                                                    className="form-control"
                                                    rows={3}
                                                    placeholder="Reason for cancellation"
                                                    value={cancellationReason}
                                                    onChange={(e) => setCancellationReason(e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="hvt-wizard-footer d-flex justify-content-between">
                                        <button
                                            type="button"
                                            className="btn hvt-back-btn"
                                            onClick={() => { setCancelTarget(null); setCancellationReason(""); }}
                                            disabled={isCancelling}
                                        >
                                            Back
                                        </button>
                                        <button
                                            type="button"
                                            className="btn custom-sumbitbtn"
                                            onClick={handleConfirmCancel}
                                            disabled={isCancelling}
                                        >
                                            {isCancelling ? <FaSpinner className="fa-spin" /> : "Confirm cancellation"}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default HVTRegistrationListComponent;
